import React, { useEffect, useRef, useState } from 'react';
import { buttonClass } from '../components/ui/Button';
import { TextArea } from '../components/ui/Field';

/** The server wants five characters after trimming; counting only visible ones keeps "a b c" from passing here and failing there. */
export const MIN_VETO_CHARS = 5;
export const visibleLength = (s: string) => [...s.replace(/\s+/g, '')].length;


let seq = 0;

interface VetoComposerProps {
  /** Resolves true when the veto landed; the composer then closes itself through the parent. */
  onSubmit: (reason: string) => Promise<boolean>;
  onCancel: () => void;
}

/** A veto is an objection with its reason attached. No reason, no veto. */
const VetoComposer: React.FC<VetoComposerProps> = ({ onSubmit, onCancel }) => {
  const [reason, setReason] = useState('');
  const [tried, setTried] = useState(false);
  const [busy, setBusy] = useState(false);
  const [ids] = useState(() => {
    seq += 1;
    return { field: `np-veto-${seq}`, hint: `np-veto-${seq}-hint` };
  });
  const ref = useRef<HTMLTextAreaElement>(null);
  const tooShort = visibleLength(reason) < MIN_VETO_CHARS;
  const showError = tried && tooShort;

  useEffect(() => {
    ref.current?.focus();
  }, []);

  const submit = async () => {
    if (busy) return;
    if (tooShort) {
      setTried(true);
      ref.current?.focus();
      return;
    }
    setBusy(true);
    const ok = await onSubmit(reason.trim());
    if (!ok) setBusy(false);
  };

  return (
    <div className="rounded-md bg-red-500/10 p-2">
      <label htmlFor={ids.field} className="mb-1 flex items-center gap-1.5 text-xs font-semibold text-red-300">
        <FlagIcon className="h-3.5 w-3.5" />
        Veto this note
      </label>
      <TextArea
        id={ids.field}
        ref={ref}
        value={reason}
        rows={2}
        disabled={busy}
        onChange={(e) => setReason(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Escape') {
            e.preventDefault();
            e.stopPropagation();
            onCancel();
          } else if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            submit();
          }
        }}
        placeholder="What is wrong with it?"
        error={showError}
        aria-describedby={ids.hint}
      />
      <div className="mt-1.5 flex flex-wrap items-center gap-1">
        <button
          type="button"
          onClick={submit}
          disabled={busy}
          className={buttonClass('danger', 'sm')}
        >
          Veto
        </button>
        <button
          type="button"
          onClick={onCancel}
          disabled={busy}
          className={buttonClass('quiet', 'sm')}
        >
          Cancel
        </button>
        <span id={ids.hint} role={showError ? 'alert' : undefined} className={`ml-1 text-[11px] ${showError ? 'text-red-300' : 'text-on-surface-variant'}`}>
          {showError ? 'Say why, in a few words' : 'Its author and the people who run this plan are told.'}
        </span>
      </div>
    </div>
  );
};

/** A flag on its pole, filled. Shared with the thread's veto rows. */
export const FlagIcon: React.FC<{ className?: string }> = ({ className = 'h-4 w-4' }) => (
  <svg aria-hidden="true" viewBox="0 0 24 24" className={`flex-shrink-0 ${className}`} fill="none">
    <path fill="currentColor" d="M5 3h2v18H5zM8 4h9.5l-2 4 2 4H8z" />
  </svg>
);

export default VetoComposer;
